import { NoSsr } from "@mui/base";
import type { FunctionComponent, ReactNode } from "react";
import { useEffect, useMemo } from "react";

import { useResponsive } from "shared/hooks";
import type { Edition } from "shared/lib/edition";
import { theme } from "shared/lib/styles";

import { useIsScrollingOverAppBarThreshold } from "scmp-app/components/app-bar";
import { HideElementOnScrollEffectThreshold } from "scmp-app/components/app-bar/consts";
import { useAppBarContext } from "scmp-app/components/app-bar/contexts";
import { useAppBarSetStickyScrollHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { useHeaderFeaturesProvider } from "scmp-app/components/header/features/hooks";
import type { HeaderConfiguration } from "scmp-app/components/header/types";

import { DynamicHomeNewsTicker } from "./dynamics";
import { HomeEdition } from "./edition";
import {
  CenterFeatureContainer,
  Container,
  Content,
  HeaderContainer,
  HomeHeaderContainer,
  LeftFeatureContainer,
  OnelineMenuContainer,
  RightFeatureContainer,
  StyledHeaderOnelineMenuContainer,
  StyledHomeMiniHeader,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
  initEdition?: Edition;
};

export const HomeHeader: FunctionComponent<Props> = ({
  className,
  headerConfiguration,
  initEdition,
}) => {
  const { setShouldSwap } = useAppBarContext();
  const { reference } = useAppBarSetStickyScrollHeight();

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));
  const threshold = isDesktopUp
    ? HideElementOnScrollEffectThreshold.desktop
    : HideElementOnScrollEffectThreshold.mobile;
  const shouldSwap = useIsScrollingOverAppBarThreshold(threshold);

  useEffect(() => {
    if (!setShouldSwap) return;
    setShouldSwap(shouldSwap ?? false);
  }, [setShouldSwap, shouldSwap]);

  const headerFeatureProvider = useHeaderFeaturesProvider();

  const layout = useMemo(() => {
    const defaultLayout: { center: ReactNode; left: ReactNode; right: ReactNode } = {
      center: (
        <CenterFeatureContainer>
          {headerFeatureProvider.logoTextLink({
            showIn: "mobileUp",
            size: "large",
          })}
        </CenterFeatureContainer>
      ),
      left: (
        <LeftFeatureContainer>
          {headerFeatureProvider.hamburgerMenu({ showIn: "mobileOnly" })}
        </LeftFeatureContainer>
      ),
      right: (
        <RightFeatureContainer>
          {headerFeatureProvider.dashboard({
            showIn: "mobileOnly",
          })}
        </RightFeatureContainer>
      ),
    };

    return defaultLayout;
  }, [headerFeatureProvider]);

  const appBarStickyScrollHeight = headerConfiguration?.appBarStickyScrollHeight ?? 0;

  return (
    <>
      <Container className={className}>
        <HomeHeaderContainer ref={reference}>
          <HomeEdition />
          <HeaderContainer>
            <Wrapper>
              <NoSsr>
                <DynamicHomeNewsTicker />
              </NoSsr>
              <Content>
                {layout.left}
                {layout.center}
                {layout.right}
              </Content>
              <OnelineMenuContainer>
                <StyledHeaderOnelineMenuContainer initEdition={initEdition} />
              </OnelineMenuContainer>
            </Wrapper>
          </HeaderContainer>
        </HomeHeaderContainer>
      </Container>

      <StyledHomeMiniHeader
        $appBarStickyScrollHeight={appBarStickyScrollHeight}
        $shouldSwap={shouldSwap}
        headerConfiguration={headerConfiguration}
        initEdition={initEdition}
        isHomeMiniHeader={true}
      />
    </>
  );
};

HomeHeader.displayName = "HomeHeader";
